export const BOOT_STALL_MS = 15000;
export const SYNC_LOCK_PREFIX = "inspire.sync.";
const PERSISTENCE_DB_NAME = "inspire-persistence";

function assertFacts({ appName, path, elapsedMs, rootBooted }) {
  if (typeof appName !== "string" || appName === "") {
    throw new Error(`[q3po] describeBootStall needs the root app name, got ${JSON.stringify(appName)}`);
  }
  if (typeof path !== "string" || path === "") {
    throw new Error(`[q3po] describeBootStall needs the page path, got ${JSON.stringify(path)}`);
  }
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    throw new Error(`[q3po] describeBootStall needs elapsedMs as a non-negative number, got ${JSON.stringify(elapsedMs)}`);
  }
  if (typeof rootBooted !== "boolean") {
    throw new Error(`[q3po] describeBootStall needs rootBooted as a boolean, got ${JSON.stringify(rootBooted)}`);
  }
}

function syncLockSummary(locks) {
  if (!locks) {
    return "Web Locks: unavailable";
  }
  const isSync = lock => typeof lock?.name === "string" && lock.name.startsWith(SYNC_LOCK_PREFIX);
  const held = (locks.held ?? []).filter(isSync).map(lock => `${lock.name} held by client ${lock.clientId ?? "?"}`);
  const pending = (locks.pending ?? []).filter(isSync).map(lock => `${lock.name} pending for client ${lock.clientId ?? "?"}`);
  const entries = [...held, ...pending];
  return `Web Locks: ${entries.length > 0 ? entries.join(", ") : "no inspire.sync lock held or pending"}`;
}

export function describeBootStall(facts) {
  assertFacts(facts);
  const seconds = Math.round(facts.elapsedMs / 1000);
  const visibility = `tab visibility: ${facts.visibilityState ?? "unknown"}`;
  if (facts.rootBooted) {
    return {
      step: "page-resolution",
      message: `[q3po] ${facts.path} has not shown its page after ${seconds}s: the engine booted but AppPage never showed the page. `
        + `It is still resolving the page's providesContexts; check the /api/v1/app/${facts.appName}/entities and /feeds responses `
        + `for this URL and the [inspire] errors above (${visibility}).`,
      facts,
    };
  }
  const idb = `IndexedDB "${PERSISTENCE_DB_NAME}" available: ${facts.indexedDbAvailable ? "yes" : "no"}`;
  return {
    step: "engine-boot",
    message: `[q3po] ${facts.path} has not booted after ${seconds}s: App.create("${facts.appName}") has not resolved, so the page stays blank. `
      + "It waits on the one-tab sync election, the IndexedDB snapshot read, then the App.json modules and dependency apps. "
      + `${syncLockSummary(facts.locks)}; ${idb}; ${visibility}. `
      + "If another tab holds the lock, close it; if a fresh private window loads, this browser's site data is the broken state.",
    facts,
  };
}

export function collectBootFacts({ appName, path, elapsedMs, rootBooted, navigatorLike, documentLike, indexedDbLike }) {
  const locksApi = navigatorLike?.locks;
  const locksQuery = locksApi && typeof locksApi.query === "function" ? locksApi.query() : Promise.resolve(null);
  return Promise.resolve(locksQuery).then(locks => ({
    appName,
    path,
    elapsedMs,
    rootBooted,
    visibilityState: documentLike?.visibilityState ?? "unknown",
    indexedDbAvailable: indexedDbLike !== undefined && indexedDbLike !== null,
    locks: locks ?? null,
  }));
}

export function watchBootStall({
  timeoutMs,
  isSettled,
  collect,
  report,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = id => clearTimeout(id),
}) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new Error(`[q3po] watchBootStall needs a positive timeoutMs, got ${JSON.stringify(timeoutMs)}`);
  }
  for (const [name, fn] of Object.entries({ isSettled, collect, report })) {
    if (typeof fn !== "function") {
      throw new Error(`[q3po] watchBootStall needs a ${name} function`);
    }
  }
  let cancelled = false;
  const timer = setTimer(() => {
    if (cancelled || isSettled()) {
      return;
    }
    Promise.resolve()
        .then(collect)
        .then(
          facts => {
            if (!cancelled && !isSettled()) {
              report(describeBootStall(facts));
            }
          },
          error => report({
            step: "collect-failed",
            message: `[q3po] the page has not booted after ${Math.round(timeoutMs / 1000)}s `
              + `and the boot facts could not be gathered: ${error?.message ?? error}`,
            facts: null,
          }),
        );
  }, timeoutMs);
  return () => {
    cancelled = true;
    clearTimer(timer);
  };
}
